import { supabase } from '@/integrations/supabase/client';
import { PropertyFormData, PropertyPhoto, ListingsByLanguage, UserProfile } from '@/types/property';
import { getMarketConfig } from '@/lib/markets';

export async function generateListingWithAI(
  formData: PropertyFormData,
  photos: PropertyPhoto[],
  profile?: UserProfile | null
): Promise<ListingsByLanguage[]> {
  // Convert photos to base64 - handle both File objects and URL previews
  const imagePromises = photos.slice(0, 5).map(async (photo) => {
    // If photo has a file, use FileReader
    if (photo.file) {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = () => {
          console.warn('FileReader failed for photo, trying preview URL');
          resolve(photo.preview);
        };
        reader.readAsDataURL(photo.file);
      });
    }
    
    // If no file but has preview (URL-imported photos)
    if (photo.preview) {
      // If preview is already a data URL, return it
      if (photo.preview.startsWith('data:')) {
        let dataUrl = photo.preview;
        // Fix common MIME type issues
        if (dataUrl.includes('application/octet-stream')) {
          dataUrl = dataUrl.replace('application/octet-stream', 'image/jpeg');
        }
        return dataUrl;
      }
      
      // Try to fetch the preview URL and convert to base64
      try {
        const response = await fetch(photo.preview);
        if (response.ok) {
          const blob = await response.blob();
          let mimeType = blob.type;
          if (!mimeType || mimeType === 'application/octet-stream') {
            mimeType = 'image/jpeg';
          }
          
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              let result = reader.result as string;
              if (result.includes('application/octet-stream')) {
                result = result.replace('application/octet-stream', mimeType);
              }
              resolve(result);
            };
            reader.readAsDataURL(blob);
          });
        }
      } catch (err) {
        console.warn('Failed to fetch preview URL:', err);
      }
      
      // Last resort: return the URL directly
      return photo.preview;
    }
    
    // No file or preview - skip this photo
    return '';
  });

  const images = (await Promise.all(imagePromises)).filter(img => img !== '');

  // Validate required fields before calling the edge function
  if (!formData.city || formData.city.trim() === '') {
    throw new Error('Please select a city for the property');
  }
  if (!formData.price || formData.price.trim() === '') {
    throw new Error('Please enter a price for the property');
  }

  // Sanitize price - remove any non-digit characters (commas, currency symbols, spaces)
  const sanitizedPrice = formData.price.replace(/[^\d]/g, '');
  if (!sanitizedPrice || sanitizedPrice === '0') {
    throw new Error('Please enter a valid price for the property');
  }

  console.log('Calling generate-listing with', images.length, 'images for', formData.languages.length, 'languages');
  console.log('Property category:', formData.category, 'type:', formData.type);
  console.log('City:', formData.city, 'Address:', formData.address);

  // Truncate highlights to 1000 characters to avoid validation error
  const truncatedHighlights = formData.highlights 
    ? formData.highlights.substring(0, 1000) 
    : '';

  // Market-derived formatting overrides the profile so each listing renders
  // in the currency/unit that matches the property's market.
  const marketCfg = getMarketConfig(formData.market);

  const { data, error } = await supabase.functions.invoke('generate-listing', {
    body: {
      propertyData: {
        listingType: formData.listingType,
        category: formData.category,
        type: formData.type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        squareMeters: formData.squareMeters,
        price: sanitizedPrice,
        address: formData.address,
        city: formData.city,
        features: formData.features,
        condition: formData.condition,
        highlights: truncatedHighlights,
        languages: formData.languages,
        furnished: formData.furnished,
        useEmojis: formData.useEmojis,
        includeAgentInfo: formData.includeAgentInfo,
        // Market tailoring (consumed by the edge function when ready)
        market: formData.market,
        marketAttributes: formData.marketAttributes,
      },
      images,
      agentInfo: formData.includeAgentInfo && profile ? {
        phone: profile.phone,
        email: profile.email,
        agencyName: profile.agency_name,
        licenseNumber: profile.license_number,
      } : null,
      // Pass user preferences for description customization
      // Per-listing overrides (from formData) take precedence over profile defaults
      userPreferences: {
        tone: formData.descriptionTone || (profile?.description_tone ?? undefined),
        length: formData.descriptionLength || (profile?.description_length ?? undefined),
        customInstructions: formData.customInstructions || (profile?.description_custom_instructions ?? undefined),
        includeHashtags: formData.includeHashtags ?? profile?.include_hashtags ?? true,
        focusFeatures: formData.focusFeatures || (profile?.focus_features as string[] | undefined) || undefined,
        // Regional preferences: market wins, profile is the fallback.
        currency: marketCfg.currency || profile?.currency,
        areaUnit: marketCfg.areaUnit || profile?.area_unit,
        numberFormat: marketCfg.numberFormat || profile?.number_format,
      },
    },
  });

  if (error) {
    console.error('Edge function error:', error);
    throw new Error(error.message || 'Failed to generate listing');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data.listingsByLanguage as ListingsByLanguage[];
}

// Fallback generator for when AI is unavailable
export function generateFallbackListing(
  formData: PropertyFormData,
  profile?: UserProfile | null
): ListingsByLanguage[] {
  const priceFormatted = parseInt(formData.price).toLocaleString();
  const emoji = formData.useEmojis;
  const isResidential = formData.category === 'residential';
  const isCommercial = formData.category === 'commercial';
  const isLand = formData.category === 'land';
  
  // Type labels
  const typeLabels: Record<string, string> = {
    apartment: 'Apartment', house: 'House', villa: 'Villa', maisonette: 'Maisonette',
    townhouse: 'Townhouse', studio: 'Studio', penthouse: 'Penthouse',
    office: 'Office Space', shop: 'Retail Shop', warehouse: 'Warehouse',
    restaurant: 'Restaurant', hotel: 'Hotel', building: 'Commercial Building',
    'residential-plot': 'Residential Plot', 'commercial-plot': 'Commercial Plot',
    agricultural: 'Agricultural Land', industrial: 'Industrial Land',
  };
  const typeLabel = typeLabels[formData.type] || formData.type;
  
  // Build agent info footer
  let agentFooter = '';
  if (formData.includeAgentInfo && profile) {
    const parts = [];
    if (profile.agency_name) parts.push(profile.agency_name);
    if (profile.phone) parts.push(`Tel: ${profile.phone}`);
    if (profile.email) parts.push(profile.email);
    if (profile.license_number) parts.push(`Lic: ${profile.license_number}`);
    if (parts.length > 0) {
      agentFooter = `\n\n${emoji ? '📞 ' : ''}Contact:\n${parts.join('\n')}`;
    }
  }

  // Category-specific content
  const sizeText = formData.squareMeters ? `${formData.squareMeters} m²` : 'Spacious';
  let detailsText = '';
  let hashtagSuffix = '';

  if (isResidential) {
    const bedsText = formData.bedrooms === 0 ? 'Studio' : `${formData.bedrooms} bed`;
    const furnishedText = formData.furnished === 'fully' ? 'Fully Furnished' 
      : formData.furnished === 'partially' ? 'Partially Furnished' : '';
    
    detailsText = `${emoji ? '🛏️ ' : ''}${bedsText} | ${emoji ? '🛁 ' : ''}${formData.bathrooms} bath
${furnishedText ? `${emoji ? '🛋️ ' : ''}${furnishedText}` : ''}`;
    hashtagSuffix = '#RealEstate #PropertyForSale #DreamHome';
    
  } else if (isCommercial) {
    detailsText = `${emoji ? '📐 ' : ''}${sizeText}
${emoji ? '🏢 ' : ''}${formData.condition ? formData.condition.replace('-', ' ') : 'Ready for use'}`;
    hashtagSuffix = '#CommercialProperty #BusinessSpace #Investment';
    
  } else if (isLand) {
    detailsText = `${emoji ? '📐 ' : ''}Plot: ${sizeText}
${emoji ? '📋 ' : ''}${formData.features.includes('title-deeds') ? 'Title Deeds Available' : 'Development Potential'}`;
    hashtagSuffix = '#LandForSale #PlotForSale #RealEstateInvestment';
  }

  const listingTypeLabel = formData.listingType === 'rent' 
    ? (isLand ? 'Lease' : 'Rent') 
    : 'Sale';

  return formData.languages.map(lang => ({
    language: lang,
    listings: [
      {
        platform: 'facebook' as const,
        title: `${emoji ? (isCommercial ? '🏢 ' : isLand ? '🗺️ ' : '🏠 ') : ''}${typeLabel} for ${listingTypeLabel} in ${formData.city} - $${priceFormatted}${formData.listingType === 'rent' ? '/mo' : ''}`,
        description: `${emoji ? (isCommercial ? '🏢 ' : isLand ? '🗺️ ' : '🏠 ') : ''}${typeLabel} available now in ${formData.city}!

${emoji ? '📍 ' : ''}Location: ${formData.city}${formData.address ? ` - ${formData.address}` : ''}
${emoji ? '💰 ' : ''}Price: $${priceFormatted}${formData.listingType === 'rent' ? '/month' : ''}
${detailsText}

${formData.features.length > 0 ? `${emoji ? '✨ ' : ''}Features:\n${formData.features.slice(0, 5).map(f => `• ${f}`).join('\n')}` : ''}

${formData.highlights ? `${emoji ? '💎 ' : ''}${formData.highlights}` : ''}

${emoji ? '📸 ' : ''}Contact for viewing!${agentFooter}

#${formData.city.replace(/\s/g, '')} ${hashtagSuffix}`,
      },
      {
        platform: 'general' as const,
        title: `${typeLabel} | ${formData.city} | $${priceFormatted}`,
        description: `${isCommercial ? 'Prime commercial opportunity' : isLand ? 'Excellent development opportunity' : 'Wonderful property'} in ${formData.city}.

${isResidential ? `This ${formData.bedrooms === 0 ? 'studio' : `${formData.bedrooms} bedroom`} property offers ${formData.squareMeters ? `${formData.squareMeters} m² of` : ''} comfortable living space.` : ''}
${isCommercial ? `This ${typeLabel.toLowerCase()} offers ${formData.squareMeters ? `${formData.squareMeters} m² of` : 'ample'} space for your business needs.` : ''}
${isLand ? `This ${typeLabel.toLowerCase()} spans ${formData.squareMeters || 'a generous area'} with excellent potential.` : ''}

${formData.features.length > 0 ? `Key Features:\n${formData.features.slice(0, 5).map(f => `• ${f}`).join('\n')}` : ''}

Offered at $${priceFormatted}${formData.listingType === 'rent' ? ' per month' : ''}.${agentFooter}`,
      },
      {
        platform: 'instagram' as const,
        title: '',
        description: `${emoji ? '✨ ' : ''}${formData.listingType === 'rent' ? 'New Rental' : 'New Listing'}!${emoji ? ' ✨' : ''}

${emoji ? (isCommercial ? '🏢 ' : isLand ? '🗺️ ' : '🏠 ') : ''}${typeLabel} in ${formData.city}
${emoji ? '💰 ' : ''}$${priceFormatted}${formData.listingType === 'rent' ? '/mo' : ''}
${detailsText}

DM for details!${emoji ? ' 📩' : ''}

${hashtagSuffix} #${formData.city.replace(/\s/g, '')}`,
      },
      {
        platform: 'instagram-story' as const,
        title: '',
        description: `${formData.listingType === 'rent' ? (emoji ? '🔑 FOR ' + (isLand ? 'LEASE' : 'RENT') : 'FOR ' + (isLand ? 'LEASE' : 'RENT')) : (emoji ? '🏷️ FOR SALE' : 'FOR SALE')}

${typeLabel}
${emoji ? '📍 ' : ''}${formData.city}
${emoji ? '💰 ' : ''}$${priceFormatted}${formData.listingType === 'rent' ? '/mo' : ''}

Swipe up!`,
      },
    ],
  }));
}